import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isActiveLesson } from "../../lib/lessonLifecycle";

// Booking dates are calendar dates from the server, not UTC instants.
export function londonToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const part = type => parts.find(p => p.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export default function BookingCalendar({ bookings, selectedDate, onSelectDate }) {
  const today = londonToday();
  const [month, setMonth] = useState(() => new Date(`${today}T12:00:00`));
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1, 12);
  const days = new Date(year, monthIndex + 1, 0, 12).getDate();
  const offset = (first.getDay() + 6) % 7;
  const label = first.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const counts = new Map();
  bookings.filter(isActiveLesson).forEach(booking => {
    counts.set(booking.date, (counts.get(booking.date) || 0) + 1);
  });

  return (
    <section data-testid="pupil-calendar" aria-label="Your lesson calendar"
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(year, monthIndex - 1, 1, 12))}
          className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronLeft size={20} /></button>
        <h2 className="font-bold text-base" aria-live="polite">{label}</h2>
        <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(year, monthIndex + 1, 1, 12))}
          className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronRight size={20} /></button>
      </div>
      <div className="grid grid-cols-7 text-center gap-y-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => <span key={day} className="text-xs text-slate-500 py-1">{day}</span>)}
        {Array.from({ length: offset }, (_, i) => <span key={`blank-${i}`} aria-hidden="true" />)}
        {Array.from({ length: days }, (_, i) => {
          const day = i + 1;
          const date = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const count = counts.get(date) || 0;
          const selected = date === selectedDate;
          return <button type="button" key={date} data-testid={`calendar-date-${date}`}
            aria-label={`${String(day).padStart(2, "0")}-${String(monthIndex + 1).padStart(2, "0")}-${year}, ${count} lesson${count === 1 ? "" : "s"}`}
            aria-pressed={selected} aria-current={date === today ? "date" : undefined}
            onClick={() => onSelectDate(selected ? null : date)}
            className={`min-h-11 min-w-0 rounded-lg flex flex-col items-center justify-center text-sm ${selected ? "bg-blue-700 text-white" : date === today ? "bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            <span>{day}</span><span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${count ? selected ? "bg-white" : "bg-blue-600" : "bg-transparent"}`} />
          </button>;
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
        <span>Blue dots mark your lessons</span>
        <button type="button" className="min-h-11 px-2 font-semibold text-blue-700 dark:text-blue-300"
          onClick={() => { setMonth(new Date(`${today}T12:00:00`)); onSelectDate(null); }}>Today · show all</button>
      </div>
    </section>
  );
}
